import { FC, ReactNode, useMemo } from 'react'
import {
  Text,
  GreyGrey30,
  GreyGrey80,
  GreyWhite,
  InputSelectControl,
  Primary12Regular,
  Primary13SemiBold,
  RedRed70,
  TextAreaInput,
  TextFieldInput,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { CountriesAlpha2 } from '@sequencehq/api'
import { getStateOptions } from '@sequencehq/api/utils/commonEnums'
import { Address } from '@sequencehq/api/utils/commonModels'
import { countryOptions, resolveAddressCountryChange } from 'Invoices/utils'
import { InvoiceReviewFormValues } from '../../types'
import { REVIEW_FORM_PANEL_WIDTH } from '../../constants'
import { useIsMobile } from 'hooks/useIsMobile'
import {
  getInvoiceReviewFormErrors,
  InvoiceReviewFormErrors
} from '../../invoiceUpdateReview.helpers'

/**
 * Right pane of the review modal: the editable invoice fields the sent-invoice
 * patch permits — the billing address (country-aware, matching the invoice
 * editor's inline sent-invoice address form) plus the PO number, reference and
 * memo. Controlled by the parent so every edit updates both the submitted record
 * and the live document preview. Fixed width so it sits in the layout's `auto`
 * column while the preview flexes to fill the rest.
 */
export const InvoiceFieldsEditorPanel = ({
  values,
  onChange,
  showValidationErrors
}: {
  values: InvoiceReviewFormValues
  onChange: (values: InvoiceReviewFormValues) => void
  showValidationErrors: boolean
}) => {
  const errors = useMemo(() => getInvoiceReviewFormErrors(values), [values])
  const shownErrors: InvoiceReviewFormErrors = showValidationErrors
    ? errors
    : {}

  const address = values.customerBillingAddress

  const updateAddress = (update: Partial<Address>) =>
    onChange({
      ...values,
      customerBillingAddress: { ...address, ...update }
    })

  const handleCountryChange = (country: string) =>
    onChange({
      ...values,
      customerBillingAddress: resolveAddressCountryChange(
        address,
        country as CountriesAlpha2
      )
    })

  const stateOptions = useMemo(
    () => getStateOptions(address.country),
    [address.country]
  )

  const isMobile = useIsMobile()

  return (
    <Grid
      templateRows="auto 1fr"
      height="100%"
      // On mobile this pane is shown alone (the document preview is dropped) in
      // a full-width container, so the fixed panel width would overflow the
      // viewport — fill the width instead.
      width={isMobile ? '100%' : REVIEW_FORM_PANEL_WIDTH}
      overflow="hidden"
      backgroundColor={GreyWhite}
      data-testid="invoiceUpdateReview.editorPanel"
    >
      {/* Single, always-active "Edit" tab, matching the customer-info review's
          static tab bar. */}
      <GridItem
        display="flex"
        gap="4px"
        padding="0 12px"
        borderBottom={`1px solid ${GreyGrey30}`}
      >
        <Flex
          height="40px"
          padding="2px 8px 0"
          borderBottom={`2px solid ${GreyGrey80}`}
          color={GreyGrey80}
          {...Primary13SemiBold}
          alignItems="center"
          justifyContent="center"
          data-testid="invoiceUpdateReview.editTab"
        >
          Edit
        </Flex>
      </GridItem>

      <GridItem overflowY="auto" padding="24px">
        <Flex flexDirection="column" gap="24px">
          <Section title="Billing information">
            <Field label="Country" error={shownErrors.country} name="country">
              <InputSelectControl
                value={address.country}
                onChange={handleCountryChange}
                options={countryOptions}
                placeholder="Select country"
                filterable
                isInvalid={Boolean(shownErrors.country)}
                data-testid="invoiceUpdateReview.form.country"
              />
            </Field>

            {/* State sits directly below country so it reads as part of the
                country selection (states are country-specific). */}
            {stateOptions.length > 0 && (
              <Field label="State" error={shownErrors.state} name="state">
                <InputSelectControl
                  value={address.state ?? ''}
                  onChange={value =>
                    updateAddress({ state: value as Address['state'] })
                  }
                  options={stateOptions}
                  placeholder="Choose state..."
                  isInvalid={Boolean(shownErrors.state)}
                  data-testid="invoiceUpdateReview.form.state"
                />
              </Field>
            )}

            <Field
              label="Address line 1"
              error={shownErrors.line1}
              name="line1"
            >
              <TextFieldInput
                value={address.line1}
                onChange={e => updateAddress({ line1: e.target.value })}
                placeholder="Address line 1"
                isInvalid={Boolean(shownErrors.line1)}
                data-testid="invoiceUpdateReview.form.line1"
              />
            </Field>

            <Field label="Address line 2">
              <TextFieldInput
                value={address.line2 ?? ''}
                onChange={e =>
                  updateAddress({ line2: e.target.value || undefined })
                }
                placeholder="Address line 2 (optional)"
                data-testid="invoiceUpdateReview.form.line2"
              />
            </Field>

            <Field label="City/town" error={shownErrors.town} name="town">
              <TextFieldInput
                value={address.town}
                onChange={e => updateAddress({ town: e.target.value })}
                placeholder="City/town"
                isInvalid={Boolean(shownErrors.town)}
                data-testid="invoiceUpdateReview.form.town"
              />
            </Field>

            <Field
              label="Postal code"
              error={shownErrors.postcode}
              name="postcode"
            >
              <TextFieldInput
                value={address.postcode}
                onChange={e => updateAddress({ postcode: e.target.value })}
                placeholder="Postal code"
                isInvalid={Boolean(shownErrors.postcode)}
                data-testid="invoiceUpdateReview.form.postcode"
              />
            </Field>
          </Section>

          <Section title="Additional information">
            <Field label="PO number">
              <TextFieldInput
                value={values.purchaseOrderNumber}
                onChange={e =>
                  onChange({ ...values, purchaseOrderNumber: e.target.value })
                }
                placeholder="PO number"
                data-testid="invoiceUpdateReview.form.purchaseOrderNumber"
              />
            </Field>

            <Field label="Reference">
              <TextFieldInput
                value={values.reference}
                onChange={e =>
                  onChange({ ...values, reference: e.target.value })
                }
                placeholder="Reference"
                data-testid="invoiceUpdateReview.form.reference"
              />
            </Field>

            <Field label="Memo">
              <TextAreaInput
                value={values.memo}
                onChange={value => onChange({ ...values, memo: value })}
                placeholder="Memo"
                maxLength={10000}
                data-testid="invoiceUpdateReview.form.memo"
              />
            </Field>
          </Section>
        </Flex>
      </GridItem>
    </Grid>
  )
}

const Section: FC<{ title: string; children: ReactNode }> = ({
  title,
  children
}) => (
  <Flex flexDirection="column" gap="16px">
    <Text {...Primary13SemiBold} color={GreyGrey80}>
      {title}
    </Text>
    {children}
  </Flex>
)

const Field: FC<{
  label: string
  error?: string
  /** Field key, used to address the error message (`…form.<name>.error`). */
  name?: string
  children: ReactNode
}> = ({ label, error, name, children }) => (
  <Flex flexDirection="column" gap="8px">
    <Text {...Primary13SemiBold} color={GreyGrey80}>
      {label}
    </Text>
    {children}
    {error && (
      <Text
        {...Primary12Regular}
        color={RedRed70}
        data-testid={name && `invoiceUpdateReview.form.${name}.error`}
      >
        {error}
      </Text>
    )}
  </Flex>
)
